import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Split from 'react-split';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';

import useContentWidth from 'hooks/useContentWidth';
import { getSplitScreenStyles, splitScreenStyle } from 'styles/splitScreen.styles';
import { getOuterMinSizes, getOuterSplitSizes } from 'utils/documentPanes';

import {
  setDocumentPaneHostWidthAction,
  setDocumentPaneSizesAction,
} from 'store/loanApplications/actions';
import { selectDocumentPanes, selectDocumentPaneSizes } from 'store/loanApplications/selectors';

import LoanDocumentPaneGroup from './LoanDocumentPaneGroup';

const OUTER_TRACK_COUNT = 2;

/**
 * Wraps the loan-editor content so that, when document side-panes are open, the editor and the
 * document-pane group render as the two tracks of a resizable react-split. With no panes open it
 * renders the editor content untouched (inside a measuring host).
 *
 * The host's measured width is published to the store so the "open documents pane" capacity check
 * (in the header, outside this subtree) gates on the REAL content-region width — react-split applies
 * `sizes` on initial render and only enforces `minSize` while dragging, so the outer sizes are also
 * derived from that width to guarantee each pane gets its full DOCUMENT_PANE_MIN_WIDTH.
 *
 * Because the OUTER track count is always 2, opening/closing/swapping a document pane (1<->2) does
 * NOT remount the editor subtree; only entering/leaving split view (0<->1) changes its ancestry.
 * (SignalR is hoisted above this layout in LoanApplicationContent, so neither transition restarts it.)
 */
const LoanEditorSplitLayout = ({ children, renderDocuments }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const documentPanes = useSelector(selectDocumentPanes);
  const storedSizes = useSelector(selectDocumentPaneSizes);

  const classes = useMemo(() => getSplitScreenStyles(theme), [theme]);
  const { contentWidth, setRef } = useContentWidth();

  useEffect(() => {
    dispatch(setDocumentPaneHostWidthAction(contentWidth));
  }, [dispatch, contentWidth]);

  const onDragEnd = useCallback(sizes => dispatch(setDocumentPaneSizesAction(sizes)), [dispatch]);

  const paneCount = documentPanes.length;

  const sizes =
    storedSizes?.length === OUTER_TRACK_COUNT
      ? storedSizes
      : getOuterSplitSizes(paneCount, contentWidth);

  return (
    <Box ref={setRef} sx={classes.root}>
      {paneCount ? (
        <Split
          minSize={getOuterMinSizes(paneCount)}
          sizes={sizes}
          style={splitScreenStyle}
          onDragEnd={onDragEnd}
        >
          <Box sx={classes.contentBlock}>{children}</Box>

          <LoanDocumentPaneGroup panes={documentPanes} renderDocuments={renderDocuments} />
        </Split>
      ) : (
        children
      )}
    </Box>
  );
};

LoanEditorSplitLayout.propTypes = {
  renderDocuments: PropTypes.func.isRequired,
  children: PropTypes.node,
};

export default LoanEditorSplitLayout;
